import { useMemo, useState } from "react";
import type { PlannedSession } from "@runlens/shared";
import { SessionCell } from "./SessionCell";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

interface PlanCalendarProps {
  sessions: PlannedSession[];
  onSessionClick?: (session: PlannedSession) => void;
}

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface MonthData {
  year: number;
  month: number;
  label: string;
  weeks: Array<
    Array<{
      date: Date;
      session?: PlannedSession;
      isToday: boolean;
      isCurrentMonth: boolean;
    }>
  >;
}

function isSameDay(d1: Date, d2: Date): boolean {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function PlanCalendar({ sessions, onSessionClick }: PlanCalendarProps) {
  const [monthOffset, setMonthOffset] = useState(0);

  const monthData = useMemo((): MonthData => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Target month
    const targetDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();

    const label = targetDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    // Create session lookup by date
    const sessionByDate = new Map<string, PlannedSession>();
    for (const session of sessions) {
      sessionByDate.set(session.scheduledDate, session);
    }

    // Find the Monday on or before the 1st of the month
    const firstOfMonth = new Date(year, month, 1);
    const firstDay = firstOfMonth.getDay(); // 0=Sun, 1=Mon, ...
    const mondayOffset = firstDay === 0 ? -6 : 1 - firstDay;
    const calendarStart = new Date(year, month, 1 + mondayOffset);

    // Find the last day of the month
    const lastOfMonth = new Date(year, month + 1, 0);

    // Generate weeks until we've covered the last day of the month
    const weeks: MonthData["weeks"] = [];
    let current = new Date(calendarStart);

    while (current <= lastOfMonth || current.getDay() !== 1) {
      const week: MonthData["weeks"][0] = [];
      for (let d = 0; d < 7; d++) {
        const date = new Date(current);
        const dateKey = toLocalDateString(date);
        week.push({
          date,
          session: sessionByDate.get(dateKey),
          isToday: isSameDay(date, today),
          isCurrentMonth: date.getMonth() === month,
        });
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);

      // Stop if we've passed the month and completed the week
      if (current.getMonth() !== month && current.getDay() === 1) break;
    }

    return { year, month, label, weeks };
  }, [sessions, monthOffset]);

  const handlePrevMonth = () => setMonthOffset((o) => o - 1);
  const handleNextMonth = () => setMonthOffset((o) => o + 1);
  const handleToday = () => setMonthOffset(0);

  const isCurrentMonth = monthOffset === 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full flex flex-col">
      {/* Navigation header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          title="Previous month"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            {monthData.label}
          </span>
          {!isCurrentMonth && (
            <button
              type="button"
              onClick={handleToday}
              className="text-xs text-brand-600 hover:text-brand-700 font-medium"
            >
              Today
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={handleNextMonth}
          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
          title="Next month"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Day names row */}
      <div className="grid grid-cols-7 border-b border-gray-100">
        {DAY_NAMES.map((day) => (
          <div key={day} className="p-1.5 text-xs font-medium text-gray-500 text-center">
            {day}
          </div>
        ))}
      </div>

      {/* Month grid */}
      <div className="flex-1 flex flex-col">
        {monthData.weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 flex-1">
            {week.map((day, dayIndex) => (
              <div
                key={dayIndex}
                className={`p-0.5 ${!day.isCurrentMonth ? "opacity-30" : ""}`}
              >
                <SessionCell
                  session={day.session}
                  date={day.date}
                  isToday={day.isToday}
                  compact
                  onClick={
                    day.session && onSessionClick
                      ? () => onSessionClick(day.session!)
                      : undefined
                  }
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
